// Detects Step 2 source columns that can never become a cost field — IDs,
// dates, plan/tier codes, free-text descriptions — so the File Column list
// only shows the columns the user actually has to decide about. Carrier
// invoices in particular carry several of these per table (e.g. BCBS
// "Report ID" / "Coverage", Humana "Member ID Number" / "Plan" / "Type",
// "Policy Number", "Certificate Number").
//
// Deliberately conservative, because hiding the wrong column is worse than
// showing an extra one:
//   - Anything money-shaped ($, 2-decimal amounts, accounting brackets) is
//     never flagged, whatever its header says.
//   - A plain text column is only flagged when its header or values are
//     clearly code/description-like, so a person-name column the name
//     detector missed (e.g. "Contract Holder") stays visible and mappable
//     as Employee Name.
//   - Callers only hide a column still at Skip (see Step2MapColumns.tsx), and
//     hidden columns stay in columnMappings unchanged — templates, Step 3/4
//     and the import payload see exactly what they saw before.

import { parseNumber } from '../parsers';
import type { ColumnMapping } from '../wizardReducer';

export type NonImportableReason = 'identifier' | 'date' | 'code' | 'description' | 'empty';

export const NON_IMPORTABLE_REASON_LABELS: Record<NonImportableReason, string> = {
	identifier: 'ID / number',
	date: 'date',
	code: 'code',
	description: 'description',
	empty: 'empty',
};

// Header terms naming an identifier. `id$` also catches run-together headers
// like "EmpID"/"EEID". Short forms ("No", "Num", "#") only count at the end of
// the header ("Contract No", "Member #") so a cost header like "Gross (No OT)"
// isn't mistaken for one.
const IDENTIFIER_HEADER_RE =
	/\b(ids?|identifier|ssn|tin|ein|policy|certificate|cert|acct|account|report|invoice|claim|number)\b|id$|\b(no|num|nbr)\.?$|#\s*$/i;

const DATE_HEADER_RE =
	/\b(date|dob|birth|effective|eff|hired?|term(ination)?|terminated|period|month|year)\b/i;
// Headers that are nothing but a year/month — plain integers under these are
// dates ("2026", "5"), whereas "Year To Date Gross" = 1000 is an amount.
const BARE_PERIOD_HEADER_RE = /^(year|yr|month|mo|period)$/i;

// Header terms for coded/categorical or free-text columns. Only applied to
// columns with no numeric values (see detectNonImportableColumn).
const CODE_HEADER_RE =
	/\b(plan|tier|type|coverage|status|class|relationship|rel|gender|sex|cc|dept|department|division|location|state)\b/i;
const DESCRIPTION_HEADER_RE =
	/\b(description|details?|notes?|comments?|memo|reason|remarks?)\b/i;

// Written-month dates: May 01, 2026 / 01-May-2026 / May 2026.
const MONTH_DATE_RE =
	/^(\d{1,2}[\s-])?(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.?([\s,-]+\d{1,2})?[\s,-]+\d{2,4}$/i;

// Short upper-case codes: "DTP", "VIS", "FAM", "EE", "S F".
const SHORT_CODE_RE = /^[A-Z0-9]{1,4}( [A-Z0-9]{1,4})?$/;

const isMonth = (n: number) => n >= 1 && n <= 12;
const isDay = (n: number) => n >= 1 && n <= 31;

/**
 * Numeric dates with a plausible month/day — 4/15/2026, 04/15/26, 2026-04-15,
 * 05/2026 — so an SSN ("123-45-6789") or a ratio isn't mistaken for one.
 */
function isNumericDate(v: string): boolean {
	const parts = v.split(/[/.-]/);
	if (parts.length < 2 || parts.length > 3 || !parts.every((p) => /^\d+$/.test(p))) return false;
	const [a, b, c] = parts;
	const n = parts.map(Number);
	if (parts.length === 2) {
		// MM/YYYY or YYYY-MM
		return (a.length <= 2 && isMonth(n[0]) && b.length === 4) || (a.length === 4 && b.length <= 2 && isMonth(n[1]));
	}
	if (a.length === 4) return b.length <= 2 && c.length <= 2 && isMonth(n[1]) && isDay(n[2]); // YYYY-MM-DD
	if (a.length > 2 || b.length > 2 || (c.length !== 2 && c.length !== 4)) return false;
	// MM/DD/YY(YY), or DD/MM/YY(YY)
	return (isMonth(n[0]) && isDay(n[1])) || (isDay(n[0]) && isMonth(n[1]));
}

/**
 * Identifier tokens that are never amounts: "PPA837392695", "AC-55210",
 * "CI-10293", "PPA821557474 / 424848135". Every word must carry a digit (or
 * be a "/" / "-" separator), so a sentence like "Contract Removed Effective
 * 05/01/2026" doesn't qualify; no decimal point is allowed, so a
 * "1,234.00 CR"-style amount can't either.
 */
function isAlphanumericId(v: string): boolean {
	if (!/[A-Za-z]/.test(v) || !/\d/.test(v) || !/^[A-Za-z0-9\-/ ]{4,}$/.test(v)) return false;
	return v.split(/\s+/).every((token) => /\d/.test(token) || /^[-/]$/.test(token));
}

const isDateValue = (v: string) => isNumericDate(v) || MONTH_DATE_RE.test(v);

const isMoneyShaped = (v: string) =>
	/[$€£]/.test(v) || /\d\.\d{2}(?!\d)/.test(v) || /^\(.*\d.*\)$/.test(v);

const isNumber = (v: string) => !isDateValue(v) && Number.isFinite(parseNumber(v));

/**
 * Why a column can't be imported as a cost field, or null when it might be
 * (and so must stay visible).
 */
export function detectNonImportableColumn(
	mapping: Pick<ColumnMapping, 'sourceHeader' | 'sampleValues'>
): NonImportableReason | null {
	const header = String(mapping.sourceHeader ?? '').trim();
	const samples = mapping.sampleValues.map((v) => String(v ?? '').trim()).filter((v) => v !== '');

	if (samples.length === 0) return 'empty';
	if (samples.some(isMoneyShaped)) return null;

	const allDates = samples.every(isDateValue);
	if (allDates) return 'date';

	const numericCount = samples.filter(isNumber).length;
	const hasNumbers = numericCount > 0;

	// An ID-named header counts only when its numbers look like IDs (5+ digits,
	// optionally dash-grouped: "124659811", "123-45-6789") rather than amounts
	// ("Account Total" = 1000, "-150").
	if (
		IDENTIFIER_HEADER_RE.test(header) &&
		samples.filter(isNumber).every((v) => /^\d[\d-]{4,}$/.test(v))
	) {
		return 'identifier';
	}
	if (samples.every(isAlphanumericId)) return 'identifier';

	// A date-named column with no numeric values (e.g. "Q2 2026", "May"), or a
	// bare "Year"/"Month" column of small integers.
	if (DATE_HEADER_RE.test(header) && !hasNumbers) return 'date';
	if (BARE_PERIOD_HEADER_RE.test(header) && samples.every((v) => /^\d{1,4}$/.test(v))) return 'date';

	if (hasNumbers) return null;

	if (DESCRIPTION_HEADER_RE.test(header)) return 'description';
	if (CODE_HEADER_RE.test(header)) return 'code';
	if (samples.every((v) => SHORT_CODE_RE.test(v))) return 'code';

	return null;
}

/**
 * colIndex → reason for every column that is still at Skip and detected as
 * non-importable. Employee Name and mapped columns are never included.
 */
export function getHiddenColumnReasons(
	columnMappings: ColumnMapping[]
): Map<number, NonImportableReason> {
	const hidden = new Map<number, NonImportableReason>();
	for (const m of columnMappings) {
		if (m.category !== 'skip') continue;
		const reason = detectNonImportableColumn(m);
		if (reason) hidden.set(m.colIndex, reason);
	}
	return hidden;
}
